import React, { useEffect, useState, useMemo } from "react";
import { Column } from "@ant-design/charts";
import axios from "axios";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import useLogout from "../useLogout";
import "./statsRoleBased.css";

const Statistics = ({ title, apiUrl, userSpecific, variant }) => {
  const logout = useLogout();
  const [data, setData] = useState([]);
  const [selectedRange, setSelectedRange] = useState([new Date(), new Date()]);
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : {};

  const isRoleBased = variant === "roleBased";

  const toMidnightUTC = (date) => {
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    return utcDate;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (selectedRange.length !== 2) {
        return;
      }
      try {
        const startDate = toMidnightUTC(new Date(selectedRange[0]));
        const endDate = toMidnightUTC(new Date(selectedRange[1]));
        const payload = {
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
        };

        if (userSpecific) {
          payload.tester_id = user.userId;
        }

        const response = await axios.post(apiUrl, payload);
        if (response.data.inactive) {
          logout();
        }

        const optCount = response.data.opt_count;

        const currentWeekDates = [];
        const currentDateFormatted = new Date(startDate);
        while (currentDateFormatted <= endDate) {
          currentWeekDates.push(
            new Date(currentDateFormatted).toISOString().split("T")[0]
          );
          currentDateFormatted.setDate(currentDateFormatted.getDate() + 1);
        }

        const newData = currentWeekDates.map((date) => ({
          type: date,
          value: optCount[date] !== undefined ? optCount[date] : null,
        }));

        setData(newData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [selectedRange, apiUrl, userSpecific, user.userId]);

  const handleDateChange = (ranges) => {
    const { startDate, endDate } = ranges.selection;
    setSelectedRange([startDate, endDate]);
  };

  const subtitle = userSpecific
    ? "Select a date range to view your personal OTP statistics"
    : "Select a date range to view OTP statistics for the period";
  const chartTitle = userSpecific
    ? "Your OTP Count by Day"
    : "OTP Count by Day";

  const pointCount = data.length;
  const denseAxis = pointCount > 14;

  const config = useMemo(
    () => ({
      data,
      xField: "type",
      yField: "value",
      height: 360,
      autoFit: true,
      xAxis: {
        label: {
          autoRotate: denseAxis,
          autoHide: true,
          autoEllipsis: true,
          formatter: (val) => {
            const date = new Date(val);
            const day = date.getDate();
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            if (denseAxis) {
              return `${day}/${month}`;
            }
            return `${day}-${month}-${year}`;
          },
        },
      },
      yAxis: {
        label: {
          formatter: (v) =>
            v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v),
        },
      },
      style: {
        fill: isRoleBased
          ? "#38bdf8"
          : ({ type }) => {
              if (type === "10-30" || type === "30") {
                return "#22CBCC";
              }
              return "#2989FF";
            },
      },
      label: {
        text: (originData) => {
          const val = parseFloat(originData.value);
          if (val != null && !Number.isNaN(val) && val >= 0) {
            return val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(val);
          }
          return "";
        },
        offset: 8,
        position: "top",
      },
      legend: false,
    }),
    [data, denseAxis, isRoleBased]
  );

  return (
    <div className="stats-rb">
      <div className="stats-rb-header">
        <h1 className="stats-rb-title">{title}</h1>
        <p className="stats-rb-subtitle">{subtitle}</p>
      </div>
      <div className="stats-rb-content">
        <div className="stats-rb-date-card">
          <div className="stats-rb-date-label">Date Range</div>
          <div className="statistics-container">
            <DateRangePicker
              onChange={handleDateChange}
              moveRangeOnFirstSelection={false}
              ranges={[
                {
                  startDate: selectedRange[0],
                  endDate: selectedRange[1],
                  key: "selection",
                },
              ]}
              className="custom-date-range-picker"
            />
          </div>
        </div>
        <div className="stats-rb-chart-card">
          <h3>{chartTitle}</h3>
          <div className="stats-rb-chart-wrapper">
            <Column {...config} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
